<template>
  <div class="assignment-library-container">
    <el-row>
      <el-col :span="isGeneralAdmin || hasFilter ? 12 : 24">
        <organization-breadcrumb :items="[$t('study_sets.curriculum'), $t('assignments.class_assignment')]" />
      </el-col>
      <el-col v-if="isGeneralAdmin || hasFilter" :span="12">
          <admin-organization-switcher/>
      </el-col>
    </el-row>
    <div class="content">
      <curriculum-menu :active-entry="`/organization/${organizationId}/assignment-library`" />
      <div class="table">
        <div class="elsa-table-layout-header">
          <div class="flex justify-center items-center">
            <label class="mr-px-16" v-if="multipleSelection.length > 0">
              {{ $t('homework.assignment_lib.table_selected_label', [multipleSelection.length, tableDataCount]) }}
            </label>
            <div class="remove-btn-div" v-if="multipleSelection && multipleSelection.length > 0">
              <el-button slot="reference" @click="handleMultipleDelete" class="remove-btn">
                <font-awesome-icon
                  :icon="['fas', 'trash-alt']"
                  style="color: #eb5757; font-size: 14px; line-height: 14px"
                />
              </el-button>
            </div>
          </div>
          <div class="search-wrap">
            <el-form class="search-form mr-px-16">
              <elsa-search
                v-model="queryString"
                @search="handleSearch"
                :placeholder="$t('homework_library.search_assignment')"
              />
            </el-form>
            <div class="create-assignment">
              <router-link
                :to="'/organization/' + organizationId + '/assignment-library/new'"
                v-if="(isOrganizationAdmin || isOrganizationAssociateAdmin || isOrganizationTeacher) && !isOrganizationK12"
                tag="button"
              >
                <span>
                  {{ $t('homework_library.create_assignment') }}
                </span>
              </router-link>
            </div>
          </div>
        </div>
        <base-table-loading
          v-if="loading"
          :data="skeletonData"
        />
        <table-layout
          v-else
          :showTitle="false"
          :pager-counter="pageCounter"
          :pageSize="numberElementsPerPage"
          :total="tableDataCount"
          :currentChange="getPage"
          :current-page.sync="currentPage"
          :query-placeholder="$t('common.search')"
          :title="$t('study_sets.curriculum')"
          v-loading="loading"
        >
          <div class="table-wrapper">
            <el-table
              ref="multipleTable"
              :data="tableData"
              :empty-text="$t('common.no_values')"
              @selection-change="handleSelectionChange"
              @sort-change="sortChange"
              :default-sort="{ prop: 'created_at', order: 'descending' }"
            >
              <el-table-column type="selection" width="55"></el-table-column>
              <el-table-column
                prop="name"
                sort-by="name"
                :sort-orders="getSortOrders"
                sortable='custom'
                :label="$t('homework_library.table.assignment_name')"
              >
                <template slot="header" slot-scope="scope">
                  <el-tooltip
                    :visible-arrow="false"
                    popper-class="custom-popper"
                    :content="showSortTip(scope.column.order)"
                    placement="top"
                  >
                    <label>{{ $t('homework_library.table.assignment_name') }}</label>
                  </el-tooltip>
                </template>
                <template slot-scope="scope">
                  <router-link :to="'/organization/' + organizationId + '/assignment-library/' + scope.row.id">
                    <span style="font-size: 16px; color: #478dff" @click="setCurrentAssignment(scope.row)">
                      {{ scope.row.name }}
                    </span>
                  </router-link>
                </template>
              </el-table-column>
              <el-table-column width="200">
                <template slot="header" slot-scope="scope">
                  {{ $t('homework_library.table.lesson_type') }}
                  <el-popover
                    placement="bottom-end"
                    trigger="click"
                    width="450"
                    ref="filterLessonType"
                    v-model="showPopperFilter"
                  >
                    <div class="filter-lesson-type">
                      <el-checkbox-group v-model="filteredValue">
                        <el-checkbox-button
                          v-for="(item, index) in filterLessonTypesData"
                          :key="index"
                          :label="item.value"
                        >
                          <i v-bind:class="getGameTypeIcon(item.value)" />
                          {{ item.text }}
                        </el-checkbox-button>
                      </el-checkbox-group>
                      <div style="text-align: right; margin: 0">
                        <el-button size="mini" type="text" @click="handleFilter(true, scope)" style="color: #606266">
                          {{ $t('homework.assignment_lib.reset') }}
                        </el-button>
                        <el-button size="mini" type="text" @click="handleFilter(false, scope)"> {{ $t('homework.assignment_lib.confirm') }} </el-button>
                      </div>
                    </div>
                    <span class="el-table__column-filter-trigger" slot="reference">
                      <i class="el-icon-arrow-down"></i>
                    </span>
                  </el-popover>
                </template>
                <template slot-scope="scope">
                  <div class="icon-wrap">
                    <span class="icon" v-for="icon in scope.row.lesson_types" :key="icon">
                      <i v-bind:class="getGameTypeIcon(icon)" />
                    </span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column
                dataType="date"
                sort-by="created_at"
                prop="created_at"
                sortable='custom'
                :sort-orders="getSortOrders"
                width="200"
                align="center"
              >
                <template slot="header" slot-scope="scope">
                  <el-tooltip
                    :visible-arrow="false"
                    popper-class="custom-popper"
                    :content="showSortTip(scope.column.order)"
                    placement="top"
                  >
                    <label>{{ $t('homework_library.table.created_on') }}</label>
                  </el-tooltip>
                </template>
                <template slot-scope="scope">
                  {{ formatDate(scope.row.created_at) }}
                </template>
              </el-table-column>
              <el-table-column :label="$t('homework_library.table.recent_assigned_class')" width="200">
                <template slot-scope="scope">
                  {{ scope.row.last_assigned_group ? scope.row.last_assigned_group : 'None' }}
                </template>
              </el-table-column>
              <el-table-column v-if="this.isOrganizationAdmin || isOrganizationTeacher" align="right" width="250">
                <template slot-scope="scope">
                  <div class="flex justify-end">
                    <span class="assign-to-class" @click="handleAssignToClass(scope.row)">
                      <chip>{{ $t($i18nLabelsHelper(currentOrganizationType, `study_sets.assign_to_group`)) }}</chip>
                    </span>
                    <el-popover placement="bottom-end" trigger="click">
                      <div class="actions-list">
                        <span
                          class="action-item"
                          :class="{ 'action-item--disabled': checkDisableCopyButton(scope.row) }"
                          @click="handleDuplicate(scope.row.id)"
                        >
                          <span class="mr-px-16">
                            <font-awesome-icon icon="copy" />
                          </span>
                         {{ $t('homework_library.table.duplicate') }}
                        </span>
                        <span @click="handleDelete(scope.row.id)" class="remove">
                          <span class="mr-px-16">
                            <font-awesome-icon :icon="['fas', 'trash-alt']" style="color: #EB5757" />
                          </span>
                          {{ $t("homework_library.table.remove") }}
                        </span>
                      </div>
                      <el-button slot="reference">
                        <i class="el-icon-more"></i>
                      </el-button>
                    </el-popover>
                  </div>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </table-layout>
      </div>
    </div>
    <assign-to-class-wrapper ref="assignToClass" />
    <confirm-delete
      :dialog-visible="modals.showConfirmDelete"
      append-to-body
      v-on:closed="modals.showConfirmDelete = false"
      v-on:delete="handleConfirmDelete"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import OrganizationBreadcrumb from '@/components/common/OrganizationBreadcrumb';
import Chip from '@/components/common/Chip';
import ElsaSearch from '@/components/studysets/Search';
import TableLayout from '@/components/studysets/TableLayout';
import CurriculumMenu from '@/components/studysets/CurriculumMenu';
import { NUMBER_ELEMENTS_PER_PAGE, PAGE_COUNTER, DATE_FORMAT } from '@/constants/generic';
import { always, pathOr } from 'ramda';
import { format } from 'date-fns';
import AssignToClassWrapper from '@/components/modals/homeworks/AssignToClassWrapper';
import ConfirmDelete from '@/components/modals/homeworks/ConfirmDelete';
import { successMessage, warningMessage } from '@/utils/messages';
import AdminOrganizationSwitcher from "@/components/dashboard/AdminOrganizationSwitcher";
import BaseTableLoading from '@/components/common/BaseTableLoading.vue';
import { LESSON_TYPE } from "@/constants/lessonType";

export default {
  components: {
    BaseTableLoading,
    ConfirmDelete,
    OrganizationBreadcrumb,
    ElsaSearch,
    TableLayout,
    Chip,
    AssignToClassWrapper,
    CurriculumMenu,
    AdminOrganizationSwitcher
  },
  props: {
    dateFormat: {
      type: String,
      default: DATE_FORMAT,
    },
  },
  data() {
    return {
      activeEntry: '2',
      loading: true,
      queryString: null,
      currentPage: 1,
      ordering: '-created_at',
      pagination: {
        results: [],
        count: 0,
      },
      filterLessonTypesData: [
        { text: this.$t('homework.assignment_lib.listening'), value: 'LISTEN' },
        { text: this.$t('homework.assignment_lib.pronunciation'), value: 'PRONUNCIATION' },
        { text: this.$t('homework.assignment_lib.word_stress'), value: 'WORD_STRESS' },
        { text: this.$t('homework.assignment_lib.conversation'), value: 'CONVERSATION' },
        { text: this.$t('homework.assignment_lib.intonation'), value: 'SENTENCE_STRESS' },
        { text: this.$t('homework.assignment_lib.study_sets'), value: 'STUDY_SET' },
        { text: this.$t('homework.assignment_lib.video_conversation'), value: 'VIDEO_CONVERSATION' },
        { text: this.$t('homework.assignment_lib.unscramble_word'), value: 'UNSCRAMBLE_WORD' },
        { text: this.$t('homework.assignment_lib.missing_character'), value: 'MISSING_CHARACTER' },
        { text: this.$t('homework.assignment_lib.read_aloud'), value: 'READ_ALOUD' },
        { text: this.$t('homework.assignment_lib.ai_tutor'), value: 'AI_TUTOR' },
      ],
      multipleSelection: [],
      deleteSelection: [],
      filteredValue: [],
      showPopperFilter: false,
      modals: {
        showConfirmDelete: false,
      },
      skeletonData: [
        {
          name: "Assignment Name",
          label: "Assignment Name",
          layout: [
            {
              width: "30%",
              justifyContent: "center",
              alignItems: "center",
              items: [
                {
                  width: "40px",
                  height: "40px",
                  type: "circle",
                },
              ],
            },
            {
              width: "70%",
              items: [
                {
                  type: "rectangle",
                  width: "60%",
                  height: "12px",
                },
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: " Lesson Type",
          label: " Lesson Type",
          layout: [
            {
              width: "100%",
              flexDirection: 'row',
              items: [
                {
                  width: "40px",
                  height: "40px",
                  type: "circle",
                },
                {
                  width: "40px",
                  height: "40px",
                  type: "circle",
                },
                {
                  width: "40px",
                  height: "40px",
                  type: "circle",
                },
                {
                  width: "40px",
                  height: "40px",
                  type: "circle",
                }
              ],
            },
          ],
        },
        {
          name: "Created On",
          label: "Created On",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Recent Assigned Class",
          label: "Recent Assigned Class",
          layout: [
            {
              width: "100%",
              flexDirection: 'row',
              items: [
                {
                  width: "80%",
                  height: "12px",
                  type: "rectangle",
                },
                {
                  width: "20%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        }
      ]
    };
  },
  computed: {
    ...mapGetters('user', ['isElsaAdmin']),
    ...mapGetters('classes', ['getCurrentClass']),
    ...mapGetters('organization', [
      'isOrganizationAdmin',
      'isOrganizationAssociateAdmin',
      'currentOrganizationType',
      'isOrganizationTeacher',
      'isGeneralAdmin',
      'hasFilter',
      'isOrganizationK12'
    ]),
    ...mapGetters({
      currentOrganizationType: 'organization/currentOrganizationType',
      currentOrganizationId: 'organization/currentOrganizationId',
      userIsTeacher: 'organization/isOrganizationTeacher',
      userIsAdmin: 'organization/isOrganizationAdmin',
      userIsAssociateAdmin: 'organization/isOrganizationAssociateAdmin',
      currentUserId: 'user/getUserId',
    }),
    organizationId() {
      return this.$store.state.organization.currentOrganizationId;
    },
    tableDataCount() {
      return pathOr(0, ['pagination', 'count'])(this);
    },
    tableData() {
      return pathOr([], ['pagination', 'results'])(this);
    },
    pageCounter: always(PAGE_COUNTER),
    numberElementsPerPage: always(NUMBER_ELEMENTS_PER_PAGE),
    getSortOrders() {
      return ['ascending', 'descending'];
    },
  },
  methods: {
    ...mapActions({
      setCurrentAssignment: 'homeworks/setCurrentAssignment',
      deleteAssignmentLibrary: 'homeworks/deleteAssignmentLibrary',
    }),
    showSortTip(order) {
      let txt = this.$t('homework.assignment_lib.click_to_sort');
      if (order === 'ascending' || order === 'descending') txt = this.$t('homework.assignment_lib.reverse_sort_direction');
      return txt;
    },
    handleFilter(isReset) {
      this.showPopperFilter = false;
      if (isReset) {
        this.filteredValue = [];
      }
      const lesson_types = this.filteredValue;
      this.getData({ lesson_types });
    },
    handleAssignToClass(row) {
      this.setCurrentAssignment(row);
      this.$refs.assignToClass.handleAssignToClass(row);
    },
    getGameTypeIcon(type) {
      let className;
      switch (type) {
        case 'LISTEN':
          className = 'elsa-game-type-listen';
          break;
        case 'CONVERSATION':
          className = 'elsa-game-type-conversation';
          break;
        case 'PRONUNCIATION':
          className = 'elsa-game-type-pronunciation';
          break;
        case 'SENTENCE_STRESS':
          className = 'elsa-game-type-sentence-stress';
          break;
        case 'WORD_STRESS':
          className = 'elsa-game-type-word-stress';
          break;
        case 'VIDEO_CONVERSATION':
          className = 'elsa-game-type-video-conversation';
          break;
        case 'UNSCRAMBLE_WORD':
          className = 'elsa-game-type-unscramble-word';
          break;
        case 'MISSING_CHARACTER':
          className = 'elsa-game-type-missing-character';
          break;
        case 'READ_ALOUD':
          className = 'elsa-game-type-read-aloud';
          break;
        case 'STUDY_SET':
          className = 'elsa-study-set';
          break;
        case 'AI_TUTOR':
          className = 'elsa-ai-lesson';
          break;
        case 'ASSESSMENT':
          className = 'elsa-game-type-assessment';
          break;
        default:
          className = 'elsa-study-set';
          break;
      }
      return className;
    },
    formatDate(date) {
      return date ? format(new Date(date), this.dateFormat) : '--';
    },
    handleSelectionChange(val) {
      this.multipleSelection = val;
    },
    getPage(page) {
      const search = this.queryString;
      return this.getData({ page, search });
    },
    handleSearch(search) {
      return this.getData({ search });
    },
    handleDelete(id) {
      this.deleteSelection = [id];
      this.modals.showConfirmDelete = true;
    },
    handleMultipleDelete() {
      this.deleteSelection = this.multipleSelection.map((x) => x.id)
      this.modals.showConfirmDelete = true
    },
    async getData({ page = 1, search = null, lesson_types = [] }) {
      this.loading = true;
      let ordering = this.ordering;
      let params = { page, search, lesson_types, ordering };
      const { data } = await this.$axios.get(`/v1/homeworks/${this.organizationId}/assignment-library`, { params });
      this.currentPage = page;
      this.pagination = data;
      this.loading = false;
    },
    async handleDuplicate(id) {
      return this.$router.push({
        name: 'assignment-library-duplicate',
        params: {
          assignmentLibraryId: id,
        },
      });
    },
    async handleConfirmDelete() {
      try {
        this.loading = true;
        for (let idx = 0; idx < this.deleteSelection.length; idx++) {
          await this.deleteAssignmentLibrary(this.deleteSelection[idx]);
        }
        successMessage(this.$t('homework_library.remove_success'))(this);
      } catch (error) {
        return warningMessage(this.$t('homework_library.remove_warning'))(this);
      } finally {
        this.multipleSelection = [];
        await this.getPage(this.currentPage);
        this.loading = false;
      }
    },
    async sortChange({prop, order }) {
      const search = this.queryString;
      if(order == "descending")
        this.ordering = '-' + prop;
      else
        this.ordering = prop;
      try {
        await this.getData({search});
      } catch (error) {
        // console.error(error);
      }
    },
    checkDisableCopyButton(rowData) {
      if (!rowData.lesson_types || !rowData.lesson_types.length) {
        return false
      }

      const { ASSESSMENT, AI_TUTOR, READ_ALOUD } = LESSON_TYPE
      return rowData.lesson_types.some(type => [ASSESSMENT, AI_TUTOR, READ_ALOUD].includes(type))
    }
  },
  async mounted() {
    try {
      const params = {};
      await this.getData(params);
    } catch (error) {
      // console.error(error);
    }
  },
};
</script>

<style lang="scss" scoped>
@import '../../scss/_colors.scss';
.assignment-library-container {
  width: 100%;

  .content {
    margin: 24px 40px;

    .table {
      .elsa-table-layout-header {
        display: flex;
        flex-direction: row;
        justify-content: space-between;

        & > div {
          align-self: center;
        }

        label {
          font-family: SF Pro Display, sans-serif;
          font-style: normal;
          font-weight: 600;
          font-size: 20px;
          line-height: 20px;
          letter-spacing: 0.25px;
          color: #121131;
        }

        .search-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
          align-content: center;
        }
      }

      .el-form {
        width: auto;
        padding-bottom: 0 !important;

        .el-form-item {
          margin-bottom: 0;
        }

        &.search-form {
          margin-top: 0;
          width: 328px;
          margin-bottom: 0;
          padding-bottom: 0;
        }
      }

      ::v-deep.el-input {
        border-radius: 8px;
        height: 48px;
        align-content: center;
        align-items: center;
      }

      .el-button {
        border-radius: 8px;
      }

      .assign-to-class {
        margin-right: 8px;

        button {
          font-size: 16px;
          line-height: 24px;
          font-weight: 600;
          padding: 8px 16px;
          border: 1px solid #007eff;
        }
      }

      .elsa-table-layout {
        margin-top: 32px;
        padding: 0;

        .el-table {
          border: 1px solid #e5e5ea;
          border-radius: 12px;
        }
      }

      .icon-wrap {
        display: flex;
        flex-wrap: wrap;
        gap: 5px;

        i {
          width: 28px;
          height: 28px;
        }
      }

      ::v-deep .el-table thead tr {
        background: #f9f9f9;
      }

      ::v-deep .el-table .el-table__header-wrapper .cell {
        color: #757575;
        font-weight: 500;
        font-size: 14px;
        line-height: 20px;
      }
    }

    .create-assignment {
      button {
        background: #007eff;
        border-radius: 8px;
        border: none;
        color: #fff;
        padding: 12px 16px;
        font-size: 16px;
        line-height: 24px;
        max-width: 230px;
        cursor: pointer;
        font-weight: 600;
        font-family: 'SF Pro Display', sans-serif;
      }
    }
  }
}

.actions-list {
  span.remove {
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0.25px;
    color: #eb5757;
  }
}

.filter-lesson-type {
  .el-checkbox-group {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
  }

  i {
    width: 24px;
    height: 24px;
    margin-right: 12px;
    justify-content: center;
    align-items: unset;
  }

  ::v-deep .el-checkbox-button {
    margin-right: 16px;
    margin-bottom: 16px;
    border: unset;
  }

  ::v-deep .el-checkbox-button__inner {
    display: flex;
    align-items: center;
    font-size: 14px;
    line-height: 20px;
    color: #121131;
    padding: 8px;
    border: 1px solid #e5e5ea;
    border-radius: 8px;
    box-shadow: unset;
  }

  ::v-deep .el-checkbox-button.is-checked .el-checkbox-button__inner {
    background: #efeff4;
    border: 1px solid #efeff4;
    border-radius: 8px;
    color: #121131;
    box-shadow: unset;
  }

  ::v-deep .el-checkbox-button:first-child .el-checkbox-button__inner,
  ::v-deep .el-checkbox-button:last-child .el-checkbox-button__inner {
    border-radius: 8px;
  }
}

::v-deep .el-table__column-filter-trigger i {
  font-size: 20px;
  color: $black;
}

::v-deep .el-table__column-filter-trigger .el-icon-arrow-down:before {
  content: '\e790';
}

::v-deep .elsa-chip {
  text-transform: unset;
}

::v-deep .el-table .el-table-column--selection .cell {
  padding-left: 20px;
}

.action-item {
  &--disabled {
    pointer-events: none;
    color: #c0c4cc;

    span {
      color: inherit;
    }
  }
}
</style>

<style>
.custom-popper.el-tooltip__popper .popper__arrow,
.custom-popper.el-tooltip__popper .popper__arrow::after {
  border-top-color: #ffffff !important;
}
</style>
